import * as React from "react";
import { RouterLink as Link } from "../router";
import { Card } from "../card";
import { Icon } from "../icon";
import { Band, Col } from "./grid";
import { Section } from "./section";
import { CommitmentLedger } from "./commitment-ledger";
import type { CommitmentTableRow } from "./commitment-table";

/**
 * SectionDashboard — a generated overview for a SECTION landing (UI-UX-PATTERNS-PRD
 * Issue 15: "a section landing is a DASHBOARD, not a bar-replay").
 *
 * A mode ROOT (`/product`) already has a real dashboard (its `dashboardSlot`). A
 * SECTION landing (`/product/roadmap`) used to fall through to a bare `ObjectsTable`
 * — no orientation, no sub-sections, no attention. This block gives every section a
 * dashboard-caliber landing generated from the section's own registry data — never
 * a replay of the Pulse bar above it (which stays as the persistent header):
 *
 *   ① Explore — the section's sub-sections as navigable entry CARDS (the views of
 *      the domain, `/mode/section/view`), so the landing orients you to what the
 *      section contains and where to go next.
 *   ② Objects — the section's own commitment inventory (`CommitmentLedger`, the
 *      SAME queue-grammar ledger the Lane cockpit uses), capped, each row clickable
 *      to its object page.
 *
 * PURE presentational: the caller (`mode-page.tsx`) computes the entries + rows and
 * passes them in — the block adds no data source of its own (aggregate-don't-fork).
 * A section with a bespoke dashboard mount keeps it; this is the generic fallback so
 * NO section ever dead-ends on a bare table or a bar-replay.
 */

export interface SectionDashboardEntry {
  /** Stable key (the view slug). */
  key: string;
  /** Card title — the sub-section's canonical label. */
  label: string;
  href: string;
  /** Icon node (the caller resolves the string key → JSX). */
  icon?: React.ReactNode;
  /** One muted line describing what this sub-section is. */
  hint?: string;
}

export function SectionDashboard({
  entries,
  rows,
  from,
  objectsLabel = "Objects",
  objectsAside = "Everything in this section",
  exploreLabel = "Explore",
  rowsLimit = 8,
  emptyObjects,
}: {
  /** The sub-sections to surface as entry cards (the domain's views). */
  entries: SectionDashboardEntry[];
  /** The section's commitment rows (the objects overview). */
  rows: CommitmentTableRow[];
  /** Mode stamped on each `/o/<uid>` link so the chrome stays anchored. */
  from?: string;
  objectsLabel?: string;
  objectsAside?: React.ReactNode;
  exploreLabel?: string;
  rowsLimit?: number;
  emptyObjects?: React.ReactNode;
}) {
  return (
    <Band>
      {entries.length > 0 ? (
        <Section label={exploreLabel}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {entries.map((e) => (
              <Card key={e.key} interactive asChild>
                <Link href={e.href} className="flex items-start gap-3 p-4">
                  {e.icon ? (
                    <span className="mt-0.5 text-muted-foreground">{e.icon}</span>
                  ) : null}
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="truncate type-body font-medium text-foreground">
                      {e.label}
                    </span>
                    {e.hint ? (
                      <span className="truncate type-subtext text-muted-foreground">
                        {e.hint}
                      </span>
                    ) : null}
                  </span>
                  <Icon name="chevron-right" size="sm" className="mt-0.5 text-muted-foreground/60" />
                </Link>
              </Card>
            ))}
          </div>
        </Section>
      ) : null}

      <Section label={objectsLabel} aside={objectsAside}>
        <CommitmentLedger
          rows={rows}
          from={from}
          limit={rowsLimit}
          empty={emptyObjects ?? "Nothing in this section yet."}
        />
      </Section>
    </Band>
  );
}
